/**
 * Power Overview Chart - Combined Timeline Visualization
 *
 * Award-winning UX combining:
 * - Power generation timeline
 * - State regions (Running, Safe, Shutdown)
 * - Mode indicators (Inertial, Sun, Nadir, Velocity, Ground)
 * - Eclipse periods
 * - Event markers with dots on timeline
 * - Interactive tooltips
 * - Status indicators
 *
 * @module @zendir/ui/charts/PowerOverviewChart
 */

import React, { useMemo } from "react";
import { AstroChart } from "./AstroChart";
import type { AstroChartProps, ChartStatus } from "./types";
import { useTheme } from "../../theme";

// =============================================================================
// Status Shape SVGs - Astro UX compliant (color + shape for accessibility)
// =============================================================================

const STATUS_BORDER_COLORS: Record<ChartStatus, string> = {
  off: "#3c3e42",
  standby: "#285766",
  normal: "#005a00",
  caution: "#645600",
  serious: "#664618",
  critical: "#661102",
};

interface StatusShapeProps {
  status: ChartStatus;
  size: number;
  color: string;
  borderColor?: string;
  glow?: boolean;
}

const StatusShape = ({
  status,
  size,
  color,
  borderColor,
  glow = true,
}: StatusShapeProps): React.ReactElement => {
  const hasBorder = !!borderColor;
  const strokeWidth = hasBorder ? 1 : 0;
  const glowFilter =
    glow && !hasBorder ? `drop-shadow(0 0 4px ${color})` : undefined;

  const renderShape = () => {
    switch (status) {
      case "off":
        return (
          <circle
            cx="6"
            cy="6"
            r={hasBorder ? 2.5 : 3}
            fill={color}
            stroke={borderColor}
            strokeWidth={strokeWidth}
          />
        );
      case "standby":
        return (
          <circle
            cx="6"
            cy="6"
            r="3.5"
            fill="none"
            stroke={color}
            strokeWidth="2"
          />
        );
      case "normal":
        return (
          <circle
            cx="6"
            cy="6"
            r={hasBorder ? 4.5 : 5}
            fill={color}
            stroke={borderColor}
            strokeWidth={strokeWidth}
          />
        );
      case "caution":
        return (
          <rect
            x={hasBorder ? 1.5 : 1}
            y={hasBorder ? 1.5 : 1}
            width={hasBorder ? 9 : 10}
            height={hasBorder ? 9 : 10}
            fill={color}
            stroke={borderColor}
            strokeWidth={strokeWidth}
          />
        );
      case "serious":
        return (
          <polygon
            points="6,1 11,6 6,11 1,6"
            fill={color}
            stroke={borderColor}
            strokeWidth={strokeWidth}
          />
        );
      case "critical":
        return (
          <polygon
            points="6,11 1,2 11,2"
            fill={color}
            stroke={borderColor}
            strokeWidth={strokeWidth}
          />
        );
      default:
        return null;
    }
  };

  return (
    <svg
      viewBox="0 0 12 12"
      width={size}
      height={size}
      style={{ filter: glowFilter }}
    >
      {renderShape()}
    </svg>
  );
};

// =============================================================================
// Types
// =============================================================================

export type PowerState = "running" | "safe" | "shutdown";
export type OperationalMode =
  | "none"
  | "inertial"
  | "sun"
  | "nadir"
  | "velocity"
  | "ground";
export type EventType = "scenario" | "operator" | "spacecraft" | "ground";

export interface PowerTimelinePoint {
  /** Timestamp (Unix ms or Date) */
  time: number | Date;
  /** Power generated (W) */
  power: number;
  /** Battery percentage (0-100) */
  battery?: number;
  /** System state */
  state: PowerState;
  /** Operational mode */
  mode: OperationalMode;
  /** Eclipse flag */
  eclipse?: boolean;
}

export interface PowerEvent {
  /** Event timestamp */
  time: number | Date;
  /** Event type */
  type: EventType;
  /** Event label */
  label: string;
  /** Optional metadata */
  meta?: Record<string, unknown>;
}

export interface PowerOverviewChartProps {
  /** Timeline data points */
  data: PowerTimelinePoint[];
  /** Event markers */
  events?: PowerEvent[];
  /** Chart title */
  title?: string;
  /** Chart height */
  height?: number | string;
  /** Show battery as secondary series */
  showBattery?: boolean;
  /** Show legend */
  showLegend?: boolean;
  /** Enable export */
  enableExport?: boolean;
  /** Custom class name */
  className?: string;
  /** Compact mode */
  compact?: boolean;
  /** Show event dots on timeline */
  showEventDots?: boolean;
  /** Show status indicator */
  showStatus?: boolean;
  /** Enable zoom controls */
  enableZoom?: boolean;
}

// =============================================================================
// Color Schemes
// =============================================================================

const STATE_COLORS: Record<PowerState, string> = {
  running: "#0d4d4d", // Deep teal
  safe: "#8b7355", // Brown/gold
  shutdown: "#1a1a1a", // Near black
};

const MODE_COLORS: Record<OperationalMode, string> = {
  none: "#1a2332",
  inertial: "#1e3a5f",
  sun: "#8b7355",
  nadir: "#2d5a4d",
  velocity: "#2e4a7c",
  ground: "#4a3d6e",
};

const EVENT_COLORS: Record<EventType, string> = {
  scenario: "#ffffff",
  operator: "#ff6b9d",
  spacecraft: "#a78bfa",
  ground: "#4ade80",
};

const ECLIPSE_COLOR = "rgba(10, 10, 15, 0.6)";

// =============================================================================
// PowerOverviewChart Component
// =============================================================================

export function PowerOverviewChart(props: PowerOverviewChartProps) {
  const {
    data,
    events = [],
    title = "Power - Power Generated",
    height = 300,
    showBattery = false,
    showLegend = false,
    enableExport = true,
    className,
    compact = false,
    showEventDots = true,
    showStatus = true,
    enableZoom = true,
  } = props;

  const { tokens, mode } = useTheme();

  // Calculate current status from latest data
  const currentStatus = useMemo(() => {
    if (!data || data.length === 0) return "off";
    const latest = data[data.length - 1];
    if (latest.state === "shutdown") return "critical";
    if (latest.state === "safe") return "caution";
    if (latest.eclipse) return "serious";
    return "normal";
  }, [data]);

  const chartConfig = useMemo<AstroChartProps>(() => {
    if (!data || data.length === 0) {
      return {
        type: "line",
        series: [],
        height,
      };
    }

    // Extract time range
    const times = data.map((d) =>
      typeof d.time === "number" ? d.time : d.time.getTime(),
    );
    const minTime = Math.min(...times);
    const maxTime = Math.max(...times);

    // Build state regions with proper colors
    const stateRegions: Array<
      [{ xAxis: number; itemStyle?: { color: string } }, { xAxis: number }]
    > = [];
    let currentState = data[0].state;
    let regionStart = minTime;

    data.forEach((point, idx) => {
      const pointTime =
        typeof point.time === "number" ? point.time : point.time.getTime();

      if (point.state !== currentState || idx === data.length - 1) {
        const regionEnd = idx === data.length - 1 ? maxTime : pointTime;
        stateRegions.push([
          {
            xAxis: regionStart,
            itemStyle: { color: STATE_COLORS[currentState] },
          },
          { xAxis: regionEnd },
        ]);
        regionStart = pointTime;
        currentState = point.state;
      }
    });

    // Build eclipse regions (overlay on top of state regions)
    const eclipseRegions: Array<
      [{ xAxis: number; itemStyle?: { color: string } }, { xAxis: number }]
    > = [];
    let inEclipse = false;
    let eclipseStart = minTime;

    data.forEach((point, _idx) => {
      const pointTime =
        typeof point.time === "number" ? point.time : point.time.getTime();

      if (point.eclipse && !inEclipse) {
        eclipseStart = pointTime;
        inEclipse = true;
      } else if (!point.eclipse && inEclipse) {
        eclipseRegions.push([
          {
            xAxis: eclipseStart,
            itemStyle: { color: ECLIPSE_COLOR },
          },
          { xAxis: pointTime },
        ]);
        inEclipse = false;
      }
    });

    // If still in eclipse at the end
    if (inEclipse) {
      eclipseRegions.push([
        {
          xAxis: eclipseStart,
          itemStyle: { color: ECLIPSE_COLOR },
        },
        { xAxis: maxTime },
      ]);
    }

    // Build series
    const series: AstroChartProps["series"] = [
      {
        id: "power",
        name: "Power (W)",
        data: data.map((d) => ({
          time: typeof d.time === "number" ? d.time : d.time.getTime(),
          value: d.power,
        })),
        color: "#ffffff",
        lineStyle: { width: 2 },
        areaStyle: { opacity: 0.3, color: "rgba(255, 255, 255, 0.2)" },
        smooth: true,
        yAxisIndex: 0,
      },
    ];

    if (showBattery) {
      series.push({
        id: "battery",
        name: "Battery %",
        data: data.map((d) => ({
          time: typeof d.time === "number" ? d.time : d.time.getTime(),
          value: d.battery ?? 0,
        })),
        color: "#4ade80",
        lineStyle: { width: 2, type: "dashed" },
        smooth: true,
        yAxisIndex: 1,
      });
    }

    // Event markers - use dots for timeline, diamonds for emphasis
    const maxPower = Math.max(...data.map((d) => d.power), 100);

    // Event dots positioned just above the chart area
    const eventDots = showEventDots
      ? events.map((event) => ({
          coord: [
            typeof event.time === "number" ? event.time : event.time.getTime(),
            maxPower * 0.95,
          ] as [number, number],
          name: event.label,
          symbol: event.type === "scenario" ? "diamond" : "circle",
          symbolSize: event.type === "scenario" ? 10 : 8,
          itemStyle: {
            color: EVENT_COLORS[event.type],
            borderColor: tokens.colors.background.base,
            borderWidth: 2,
            shadowBlur: 4,
            shadowColor: EVENT_COLORS[event.type],
          },
          label: {
            show: false,
          },
          emphasis: {
            scale: true,
            itemStyle: {
              borderWidth: 3,
              shadowBlur: 8,
            },
            label: {
              show: true,
              fontSize: 12,
              color: tokens.colors.text.primary,
              backgroundColor: tokens.colors.background.surface,
              borderColor: EVENT_COLORS[event.type],
              borderWidth: 1,
              padding: [6, 10],
              borderRadius: 6,
              formatter: `{b}`,
            },
          },
        }))
      : [];

    // Dual axis config
    const yAxisConfig: AstroChartProps["yAxis"] = showBattery
      ? [
          {
            type: "value",
            name: "Power (W)",
            position: "left",
            min: 0,
            splitLine: { show: true },
          },
          {
            type: "value",
            name: "Battery %",
            position: "right",
            min: 0,
            max: 100,
            splitLine: { show: false },
          },
        ]
      : {
          type: "value",
          name: "Power (W)",
          min: 0,
          splitLine: { show: true },
        };

    return {
      type: "area",
      title,
      subtitle: compact
        ? undefined
        : `System states, operational modes, eclipse periods, and events`,
      height,
      series,
      xAxis: {
        type: "time",
        showGrid: true,
        splitLine: { show: true },
      },
      yAxis: yAxisConfig,
      tooltip: {
        enabled: true,
        trigger: "axis",
        crosshair: "both",
        formatter: (params: any) => {
          const param = Array.isArray(params) ? params[0] : params;
          if (!param) return "";

          const time = new Date(param.data[0]);
          const dataPoint = data.find((d) => {
            const t = typeof d.time === "number" ? d.time : d.time.getTime();
            return t === param.data[0];
          });

          let html = `<div style="font-size: 13px; min-width: 220px; font-family: ${tokens.typography.fontFamily.primary};">`;
          html += `<div style="font-weight: 600; margin-bottom: 10px; color: ${tokens.colors.text.primary}; font-size: 14px;">${time.toLocaleTimeString()}</div>`;

          // Power values
          if (Array.isArray(params)) {
            params.forEach((p: any) => {
              const value = p.data[1];
              const seriesName = p.seriesName;
              const color = p.color;
              html += `<div style="display: flex; align-items: center; gap: 8px; margin-bottom: 6px;">`;
              html += `<span style="width: 12px; height: 12px; background: ${color}; border-radius: 3px; box-shadow: 0 0 4px ${color};"></span>`;
              html += `<span style="color: ${tokens.colors.text.secondary};">${seriesName}: <strong style="color: ${tokens.colors.text.primary};">${value.toFixed(1)}</strong></span>`;
              html += `</div>`;
            });
          } else {
            const value = param.data[1];
            html += `<div style="margin-bottom: 6px; color: ${tokens.colors.text.secondary};">Power: <strong style="color: ${tokens.colors.text.primary};">${value.toFixed(1)} W</strong></div>`;
          }

          // System info
          if (dataPoint) {
            html += `<div style="margin-top: 10px; padding-top: 10px; border-top: 1px solid ${tokens.colors.border.muted};">`;
            html += `<div style="display: grid; grid-template-columns: auto 1fr; gap: 8px 12px; font-size: 12px;">`;
            html += `<span style="color: ${tokens.colors.text.muted};">STATE</span>`;
            html += `<span style="color: ${STATE_COLORS[dataPoint.state]}; font-weight: 600;">${dataPoint.state.toUpperCase()}</span>`;
            html += `<span style="color: ${tokens.colors.text.muted};">MODE</span>`;
            html += `<span style="color: ${MODE_COLORS[dataPoint.mode]}; font-weight: 600;">${dataPoint.mode.toUpperCase()}</span>`;
            if (dataPoint.battery !== undefined) {
              html += `<span style="color: ${tokens.colors.text.muted};">BATTERY</span>`;
              html += `<span style="color: ${tokens.colors.text.primary}; font-weight: 600;">${dataPoint.battery.toFixed(1)}%</span>`;
            }
            html += `</div>`;
            if (dataPoint.eclipse) {
              html += `<div style="color: ${tokens.colors.status.caution}; font-weight: 600; margin-top: 8px; font-size: 12px; display: flex; align-items: center; gap: 6px;">`;
              html += `<span style="font-weight: 600;">ECLIPSE</span>`;
              html += `</div>`;
            }
            html += `</div>`;
          }

          html += `</div>`;
          return html;
        },
      },
      legend: showLegend
        ? {
            show: true,
            position: "top",
            orient: "horizontal",
          }
        : { show: false },
      zoom: enableZoom
        ? {
            enabled: true,
            type: "both",
            axis: "x",
            start: 0,
            end: 100,
          }
        : undefined,
      annotations: {
        markAreas: {
          data: [...stateRegions, ...eclipseRegions],
          itemStyle: {
            opacity: 1,
          },
        },
        markPoints: eventDots,
      },
      export: enableExport
        ? {
            enabled: true,
            formats: ["png", "svg", "csv"],
            getExportFileName: () => `zendir-power-overview-${Date.now()}`,
          }
        : undefined,
      infoTooltip: compact
        ? undefined
        : {
            content: (
              <div style={{ fontSize: "13px", lineHeight: "1.6" }}>
                <div style={{ marginBottom: "8px", fontWeight: 500 }}>
                  Interactive Power Timeline
                </div>
                <div style={{ marginBottom: "4px" }}>
                  • Background colors show system states
                </div>
                <div style={{ marginBottom: "4px" }}>
                  • Dark overlays indicate eclipse periods
                </div>
                <div style={{ marginBottom: "4px" }}>
                  • Diamond markers show key events
                </div>
                <div>• Scroll to zoom, drag to pan</div>
              </div>
            ),
          },
      className,
    };
  }, [
    data,
    events,
    title,
    height,
    showBattery,
    showLegend,
    enableExport,
    className,
    compact,
    showEventDots,
    enableZoom,
    tokens,
  ]);

  return (
    <div style={{ position: "relative", width: "100%" }}>
      {showStatus && (
        <div
          style={{
            position: "absolute",
            top: 10,
            right: 60,
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "6px 12px",
            background: tokens.colors.background.surface,
            border: `1px solid ${tokens.colors.border.default}`,
            borderRadius: tokens.borderRadius.md,
            fontSize: tokens.typography.fontSize.xs,
            fontFamily: tokens.typography.fontFamily.primary,
          }}
        >
          <StatusShape
            status={currentStatus}
            size={10}
            color={
              tokens.colors.status[currentStatus] || tokens.colors.status.off
            }
            borderColor={
              mode === "light" ? STATUS_BORDER_COLORS[currentStatus] : undefined
            }
            glow={mode !== "light"}
          />
          <span
            style={{
              color: tokens.colors.text.primary,
              fontWeight: 500,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            {currentStatus}
          </span>
        </div>
      )}
      <AstroChart {...chartConfig} />
    </div>
  );
}

// =============================================================================
// Sample Data Generator
// =============================================================================

export function generateSamplePowerData(durationSeconds = 3600): {
  data: PowerTimelinePoint[];
  events: PowerEvent[];
} {
  const dataPoints: PowerTimelinePoint[] = [];
  const eventList: PowerEvent[] = [];

  const startTime = Date.now();
  const interval = 10;

  let currentState: PowerState = "running";
  let currentMode: OperationalMode = "inertial";
  let inEclipse = false;

  for (let i = 0; i <= durationSeconds; i += interval) {
    const time = startTime + i * 1000;

    // State transitions
    if (i === 300) currentState = "safe";
    if (i === 600) currentState = "running";
    if (i === 1800) currentState = "shutdown";
    if (i === 2100) currentState = "running";
    if (i === 2400) currentState = "safe";
    if (i === 2700) currentState = "running";

    // Mode transitions
    if (i === 200) currentMode = "sun";
    if (i === 500) currentMode = "inertial";
    if (i === 800) currentMode = "nadir";
    if (i === 1100) currentMode = "velocity";
    if (i === 1400) currentMode = "ground";
    if (i === 1700) currentMode = "sun";
    if (i === 2000) currentMode = "inertial";
    if (i === 2300) currentMode = "nadir";
    if (i === 2600) currentMode = "velocity";
    if (i === 2900) currentMode = "ground";
    if (i === 3200) currentMode = "sun";

    // Eclipse periods
    if (i >= 1700 && i <= 2100) inEclipse = true;
    else if (i >= 2400 && i <= 2700) inEclipse = true;
    else inEclipse = false;

    // Battery
    let battery = 95;
    if (inEclipse) {
      battery = 95 - ((i - 1700) % 400) * 0.1;
    } else if (currentMode === "sun") {
      battery = 70 + Math.sin(i * 0.01) * 15;
    } else {
      battery = 90 + Math.sin(i * 0.005) * 5;
    }
    battery = Math.max(10, Math.min(100, battery));

    // Power
    let power = 0;
    if (inEclipse) {
      power = 0;
    } else if (currentMode === "sun") {
      power = 300 + Math.sin(i * 0.02) * 50;
    } else if (currentMode === "ground" || currentMode === "nadir") {
      power = 150 + Math.sin(i * 0.03) * 30;
    } else {
      power = 50 + Math.sin(i * 0.01) * 20;
    }

    if (currentState === "safe") power *= 0.3;
    if (currentState === "shutdown") power = 0;

    dataPoints.push({
      time,
      power,
      battery,
      state: currentState,
      mode: currentMode,
      eclipse: inEclipse,
    });
  }

  // Events
  const eventTimes = [
    250, 420, 550, 900, 1300, 1650, 1850, 2200, 2500, 2800, 3100, 3400,
  ];
  const eventTypes: EventType[] = [
    "scenario",
    "operator",
    "spacecraft",
    "ground",
  ];

  eventTimes.forEach((timeOffset, idx) => {
    eventList.push({
      time: startTime + timeOffset * 1000,
      type: eventTypes[idx % eventTypes.length],
      label: `Event ${idx + 1}`,
    });
  });

  return { data: dataPoints, events: eventList };
}

export default PowerOverviewChart;
